import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    Stack,
    CircularProgress,
    Container,
    Avatar,
    Divider,
    Snackbar,
    Alert
} from '@mui/material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import WorkIcon from '@mui/icons-material/Work';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import DescriptionIcon from '@mui/icons-material/Description';
import TranslateIcon from '@mui/icons-material/Translate';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    InputAdornment,
    TextField,
    MenuItem,
    Paper,
} from '@mui/material';
import Flag from 'react-world-flags';
import isoLanguages from './isoLanguages';
import { FileIcon, defaultStyles } from 'react-file-icon';

const getLanguageDetails = (code) => {
    if (!code) return { name: '', countryCode: 'UN' };
    return (
        isoLanguages.find((lang) => lang.code.toLowerCase() === code.toLowerCase()) || {
            name: code,
            countryCode: 'UN',
        }
    );
};

const getInitial = (str) =>
    typeof str === 'string' && str.length > 0 ? str[0].toUpperCase() : '';

const JobsBoard = () => {
    const token = useSelector((state) => state.auth.token);
    const [tasks, setTasks] = useState([]);
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [claimLoading, setClaimLoading] = useState(null); // ID of task being claimed
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // Filtering states
    const [searchQuery, setSearchQuery] = useState('');
    const [sourceLang, setSourceLang] = useState('all');
    const [targetLang, setTargetLang] = useState('all');

    // Preview Modal states
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);

    useEffect(() => {
        fetchAvailableProjects();
    }, [token]);

    const fetchAvailableProjects = async () => {
        setLoading(true);
        try {
            const res = await axios.get('/api/projects/available', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTasks(res.data);
            setFilteredTasks(res.data);
        } catch (err) {
            console.error('Error fetching jobs:', err);
            setSnackbar({ open: true, message: 'Failed to load available jobs', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let filtered = tasks;

        if (searchQuery) {
            filtered = filtered.filter(p =>
                p.projectName.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (sourceLang !== 'all') {
            filtered = filtered.filter(p => p.sourceLanguage === sourceLang);
        }

        if (targetLang !== 'all') {
            filtered = filtered.filter(p => p.targetLanguage === targetLang);
        }

        setFilteredTasks(filtered);
    }, [searchQuery, sourceLang, targetLang, tasks]);

    const handleOpenPreview = (project) => {
        setSelectedProject(project);
        setPreviewOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
        setSelectedProject(null);
    };

    const handleClaim = async (projectId) => {
        setClaimLoading(projectId);
        try {
            await axios.put(`/api/projects/${projectId}/claim`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Optional: Don't remove it, just show success? Or remove it? 
            // Usually if I claim a project I might still want to see it, but for a "Job Board" usually it disappears or marks as claimed.
            // Let's remove it for now to simulate "taking" the job.
            setTasks(prev => prev.filter(p => p._id !== projectId));
            setSnackbar({ open: true, message: 'Project claimed successfully!', severity: 'success' });
        } catch (err) {
            console.error('Error claiming project:', err);
            setSnackbar({ open: true, message: 'Failed to claim project', severity: 'error' });
        } finally {
            setClaimLoading(null);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchAvailableProjects();
    }, [token]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    const sourceLanguages = [...new Set(tasks.map(p => p.sourceLanguage))];
    const targetLanguages = [...new Set(tasks.map(p => p.targetLanguage))];

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box mb={5} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3 }}>
                <Box>
                    <Typography variant="h3" fontWeight={800} sx={{
                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}>
                        Project Hub
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                        Discover high-impact translation projects. Review requirements and claim assignments that match your expertise.
                    </Typography>
                </Box>
                <Chip icon={<WorkIcon />} label={`${filteredTasks.length} Available Opportunity${filteredTasks.length !== 1 ? 'ies' : ''}`} color="primary" variant="soft" sx={{ fontSize: '1rem', py: 2.5, px: 1, borderRadius: 2 }} />
            </Box>

            <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 4, border: '1px solid', borderColor: 'divider', background: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(10px)' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            placeholder="Search projects..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            label="Source Language"
                            value={sourceLang}
                            onChange={(e) => setSourceLang(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        >
                            <MenuItem value="all">All Languages</MenuItem>
                            {sourceLanguages.map(lang => (
                                <MenuItem key={lang} value={lang}>{lang.toUpperCase()}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            label="Target Language"
                            value={targetLang}
                            onChange={(e) => setTargetLang(e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        >
                            <MenuItem value="all">All Languages</MenuItem>
                            {targetLanguages.map(lang => (
                                <MenuItem key={lang} value={lang}>{lang.toUpperCase()}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                                setSearchQuery('');
                                setSourceLang('all');
                                setTargetLang('all');
                            }}
                            sx={{ height: 56, borderRadius: 3 }}
                        >
                            Reset Filters
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {filteredTasks.length === 0 ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    minHeight="40vh"
                    textAlign="center"
                    bgcolor="background.paper"
                    borderRadius={8}
                    p={4}
                    sx={{ border: '2px dashed', borderColor: 'divider' }}
                >
                    <WorkIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                    <Typography variant="h5" color="text.primary" fontWeight={600}>
                        No projects found
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Try adjusting your filters or search terms.
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={4}>
                    {filteredTasks.map(project => (
                        <Grid item xs={12} sm={6} md={4} key={project._id}>
                            <Card sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 5,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
                                    borderColor: 'primary.main'
                                }
                            }}>
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Stack direction="row" spacing={1} mb={2.5} alignItems="center">
                                        <Box display="flex" alignItems="center" bgcolor="primary.main" color="white" px={1} py={0.5} borderRadius={2}>
                                            <Flag code={getLanguageDetails(project.sourceLanguage).countryCode} style={{ width: 18, marginRight: 6 }} />
                                            <Typography variant="caption" fontWeight={700} mr={1}>{project.sourceLanguage?.toUpperCase()}</Typography>
                                            <Typography variant="caption" fontWeight={700} mr={1}>→</Typography>
                                            <Flag code={getLanguageDetails(project.targetLanguage).countryCode} style={{ width: 18, marginRight: 6 }} />
                                            <Typography variant="caption" fontWeight={700}>{project.targetLanguage?.toUpperCase()}</Typography>
                                        </Box>
                                        <Box sx={{ width: 22, display: 'flex', alignItems: 'center' }}>
                                            <FileIcon 
                                                extension={(project.originalFileType || '').replace('.', '').toLowerCase() || 'file'} 
                                                {...defaultStyles[(project.originalFileType || '').replace('.', '').toLowerCase()]} 
                                            />
                                        </Box>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                                            {(project.originalFileType || '').replace('.', '').toUpperCase() || 'FILE'}
                                        </Typography>
                                    </Stack>

                                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ lineHeight: 1.3 }}>
                                        {project.projectName}
                                    </Typography>

                                    <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                                        <Avatar
                                            src={
                                                project.createdBy?.avatarUrl
                                                    ? `/api/users/avatars/${project.createdBy._id}/${project.createdBy.avatarUrl.split('/').pop()}`
                                                    : ''
                                            }
                                            alt={`${project.createdBy?.firstName || ''} ${project.createdBy?.lastName || ''}`.trim()}
                                            sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'secondary.main' }}
                                        >
                                            {!project.createdBy?.avatarUrl && (getInitial(project.createdBy?.firstName) + getInitial(project.createdBy?.lastName))}
                                        </Avatar>
                                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                            {project.createdBy?.firstName} {project.createdBy?.lastName}
                                        </Typography>
                                    </Stack>

                                    <Divider sx={{ mb: 3 }} />

                                    <Grid container spacing={2} mb={1}>
                                        <Grid item xs={6}>
                                            <Box display="flex" alignItems="center" color="text.secondary">
                                                <TranslateIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                                                <Typography variant="body2" fontWeight={600}>
                                                    {project.wordCount || 0} words
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box display="flex" alignItems="center" color="text.secondary">
                                                <DescriptionIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                                                <Typography variant="body2" fontWeight={600}>
                                                    {project.pageCount || 0} pages
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box display="flex" alignItems="center" color={project.deadline && new Date(project.deadline) < new Date() ? 'error.main' : 'text.secondary'}>
                                                <AccessTimeIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                                                <Typography variant="caption" fontWeight={700}>
                                                    {project.deadline ? `Deadline: ${new Date(project.deadline).toLocaleDateString()}` : 'No deadline'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                                <Box p={3} pt={0}>
                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            onClick={() => handleOpenPreview(project)}
                                            sx={{ borderRadius: 3, fontWeight: 700, py: 1.5 }}
                                        >
                                            Preview
                                        </Button>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            onClick={() => handleClaim(project._id)}
                                            disabled={claimLoading === project._id}
                                            sx={{ borderRadius: 3, fontWeight: 700, py: 1.5, boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
                                        >
                                            {claimLoading === project._id ? <CircularProgress size={24} color="inherit" /> : 'Claim'}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Detailed Preview Dialog */}
            <Dialog
                open={previewOpen}
                onClose={handleClosePreview}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 6, p: 1 }
                }}
            >
                {selectedProject && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                            <Typography variant="h5" fontWeight={800}>Project Details</Typography>
                            <IconButton onClick={handleClosePreview} sx={{ color: 'text.secondary' }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{ mb: 4, p: 3, bgcolor: 'action.hover', borderRadius: 4 }}>
                                <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <InfoOutlinedIcon /> {selectedProject.projectName}
                                </Typography>
                                <Grid container spacing={3}>
                                    <Grid item xs={6}>
                                        <Typography variant="overline" color="text.secondary">Source Language</Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Flag code={getLanguageDetails(selectedProject.sourceLanguage).countryCode} style={{ width: 24 }} />
                                            <Typography variant="h6" fontWeight={700}>{selectedProject.sourceLanguage?.toUpperCase()}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="overline" color="text.secondary">Target Language</Typography>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Flag code={getLanguageDetails(selectedProject.targetLanguage).countryCode} style={{ width: 24 }} />
                                            <Typography variant="h6" fontWeight={700}>{selectedProject.targetLanguage?.toUpperCase()}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="overline" color="text.secondary">Capacity</Typography>
                                        <Typography variant="body1" fontWeight={600}>
                                            {selectedProject.wordCount} Words / {selectedProject.pageCount} Pages
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="overline" color="text.secondary">File Type</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 22 }}>
                                                <FileIcon 
                                                    extension={(selectedProject.originalFileType || '').replace('.', '').toLowerCase() || 'file'} 
                                                    {...defaultStyles[(selectedProject.originalFileType || '').replace('.', '').toLowerCase()]} 
                                                />
                                            </Box>
                                            <Typography variant="body1" fontWeight={600}>
                                                {(selectedProject.originalFileType || '').replace('.', '').toUpperCase() || 'DOCUMENT'}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ px: 1 }}>
                                COMPLIANCE & REQS
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3, px: 1 }}>
                                <Grid item xs={12}>
                                    <Box display="flex" alignItems="center" gap={1.5} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                        <LanguageIcon color="action" />
                                        <Typography variant="body2">Automatic translation service pre-enabled: <b>{selectedProject.translationService?.toUpperCase() || 'NONE'}</b></Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box display="flex" alignItems="center" gap={1.5} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                        <CalendarTodayIcon color="action" />
                                        <Typography variant="body2">Posted on {new Date(selectedProject.createdAt).toLocaleDateString()}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                By claiming this project, you agree to complete the translation within the deadline specified.
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ p: 3 }}>
                            <Button fullWidth variant="outlined" onClick={handleClosePreview} sx={{ borderRadius: 3, fontWeight: 700 }}>
                                Back to Board
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => {
                                    handleClaim(selectedProject._id);
                                    handleClosePreview();
                                }}
                                disabled={claimLoading === selectedProject._id}
                                sx={{ borderRadius: 3, fontWeight: 700 }}
                            >
                                {claimLoading === selectedProject._id ? <CircularProgress size={24} color="inherit" /> : 'Claim Assignment'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default JobsBoard;
